/**
 * Global time range — one persisted selection shared by every page.
 *
 * The source of truth is localStorage (`saf3ai.timeRange`), not the URL. Pages
 * used to carry the range in every URL (`?time_range=…&days=…`) because the
 * Header stamped it onto each navigation and the Atlas hook mirrored `days`;
 * both are gone. The URL now matters only as an explicit OVERRIDE — a shared
 * link, a bookmark, a chart drill-down — which is honoured for that view and
 * written through to storage.
 *
 * Stored value (JSON), one of:
 *   { "preset": "last_24_hours" | "last_7_days" | "last_30_days" | "last_90_days" }
 *   { "preset": "custom", "start_time": "<ISO>", "end_time": "<ISO>" }
 *
 * Every storage access is guarded: private mode / blocked storage must never
 * crash a page. When storage refuses a write the selection is kept in memory
 * for the tab; with nothing chosen at all, callers get their own default.
 *
 * Pure module — no React, no router. React components want
 * hooks/useGlobalTimeRange, which also re-renders on change.
 */
import { isGlobalFilterHiddenForPath } from '../components/layout/Sidebar/productConfig';

export const TIME_RANGE_STORAGE_KEY = 'saf3ai.timeRange';

/** Fired on window after every change made in this tab (other tabs get `storage`). */
export const TIME_RANGE_CHANGE_EVENT = 'saf3ai:time-range-changed';

/** The Header's default window — what pages show when nothing has been chosen. */
export const DEFAULT_TIME_RANGE = 'last_30_days';

export const TIME_RANGE_DAYS = {
  last_24_hours: 1,
  last_7_days: 7,
  last_30_days: 30,
  last_90_days: 90,
};

/**
 * URL params read as a range override. A control that commits a new range
 * strips them — left in place, the stale override would outrank the new choice.
 * `days` is legacy (Agent / Security Atlas links from before this store): still
 * read so old bookmarks open on the right window, never written.
 */
export const URL_RANGE_PARAMS = ['time_range', 'start_time', 'end_time', 'start_date', 'end_date', 'days'];

// The Header's previous key ('24h' | '7d' | '30d' | '90d'). Read as a one-way
// migration source while the new key is absent; nothing writes it any more.
const LEGACY_HEADER_KEY = 'saf3ai_header_time_range';

const DAY_MS = 86_400_000;

// Every spelling the app has used for the four presets: the backend vocabulary,
// the Header select's shorthand ('24h'), and DateRangeFilter keys ('1d').
const PRESET_ALIASES = {
  last_24_hours: 'last_24_hours',
  '24h': 'last_24_hours',
  '1d': 'last_24_hours',
  last_7_days: 'last_7_days',
  '7d': 'last_7_days',
  last_30_days: 'last_30_days',
  '30d': 'last_30_days',
  last_90_days: 'last_90_days',
  '90d': 'last_90_days',
};

/** The backend preset for any known spelling, else null. */
export const normalizeTimeRange = (value) => (
  PRESET_ALIASES[String(value ?? '').trim().toLowerCase()] || null
);

/**
 * Resolve either a platform preset or a compact day window (for example `14d`)
 * to the number of days it covers. Consumers which only need a rolling window
 * should use this instead of maintaining another partial alias table.
 */
export const daysForTimeRange = (value) => {
  const raw = String(value ?? '').trim().toLowerCase();
  const preset = normalizeTimeRange(raw);
  if (preset) return TIME_RANGE_DAYS[preset] || null;
  const compact = /^(\d+)d$/.exec(raw);
  const verbose = /(?:last_)?(\d+)_?days?$/.exec(raw);
  const days = Number(compact?.[1] || verbose?.[1]);
  return Number.isFinite(days) && days > 0 ? days : null;
};

/** Smallest preset window that covers `days` — how a custom range reaches preset-only consumers. */
export const presetForDays = (days) => {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n <= 1) return 'last_24_hours';
  if (n <= 7) return 'last_7_days';
  if (n <= 30) return 'last_30_days';
  return 'last_90_days';
};

const isTimestamp = (value) => (
  typeof value === 'string' && value !== '' && Number.isFinite(Date.parse(value))
);

/**
 * Coerce a preset string or a selection-shaped object into a valid selection,
 * or null. Everything read from storage or a URL goes through here.
 */
export const toTimeRangeSelection = (value) => {
  if (typeof value === 'string') {
    const preset = normalizeTimeRange(value);
    return preset ? { preset } : null;
  }
  if (!value || typeof value !== 'object') return null;
  if (value.preset === 'custom') {
    const { start_time: start, end_time: end } = value;
    return isTimestamp(start) && isTimestamp(end) && Date.parse(start) <= Date.parse(end)
      ? { preset: 'custom', start_time: start, end_time: end }
      : null;
  }
  const preset = normalizeTimeRange(value.preset);
  return preset ? { preset } : null;
};

/** Whole days a selection spans (custom ranges round up). */
export const selectionDays = (selection) => {
  if (!selection) return null;
  if (selection.preset !== 'custom') return TIME_RANGE_DAYS[selection.preset] || null;
  const ms = Date.parse(selection.end_time) - Date.parse(selection.start_time);
  return Math.max(1, Math.ceil(ms / DAY_MS));
};

/** A selection as a preset — a custom range maps to the smallest covering preset. */
export const selectionPreset = (selection) => {
  if (!selection) return null;
  return selection.preset === 'custom' ? presetForDays(selectionDays(selection)) : selection.preset;
};

/** YYYY-MM-DD in local time — `toISOString()` would shift the day west of UTC. */
export const toLocalDateStr = (d) => (
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
);

/**
 * A selection in the `time_range` / `start_date` / `end_date` vocabulary the
 * FinOps backend's parse_time_range accepts — keeps a custom window intact.
 */
export const selectionToDateFilters = (selection, fallback = DEFAULT_TIME_RANGE) => {
  if (selection?.preset === 'custom') {
    return {
      time_range: 'custom',
      start_date: toLocalDateStr(new Date(selection.start_time)),
      end_date: toLocalDateStr(new Date(selection.end_time)),
    };
  }
  return { time_range: selection?.preset || fallback, start_date: null, end_date: null };
};

/* ------------------------------------------------------------------ storage */

// Set only when localStorage refuses a write (private mode, blocked site data),
// and read only when localStorage refuses a read — so with storage unavailable
// a choice still carries across pages for the life of the tab.
let memorySelection = null;

const notifyChange = () => {
  try {
    window.dispatchEvent(new Event(TIME_RANGE_CHANGE_EVENT));
  } catch { /* no DOM */ }
};

/** The persisted selection, or null when nothing valid is stored. Never throws. */
export function readStoredTimeRange() {
  let raw = null;
  let legacy = null;
  try {
    raw = window.localStorage.getItem(TIME_RANGE_STORAGE_KEY);
    if (raw === null) legacy = window.localStorage.getItem(LEGACY_HEADER_KEY);
  } catch {
    return memorySelection;
  }
  if (raw !== null) {
    try {
      return toTimeRangeSelection(JSON.parse(raw));
    } catch {
      return null; // unparseable — treat as nothing stored
    }
  }
  return toTimeRangeSelection(legacy);
}

/**
 * Persist a selection (preset string or selection object) and notify every
 * subscriber. Returns false — firing nothing — when the value is invalid or
 * already stored, so write-through from many components can never loop.
 */
export function writeStoredTimeRange(next) {
  const selection = toTimeRangeSelection(next);
  if (!selection) return false;
  const serialized = JSON.stringify(selection);

  let current = null;
  try {
    current = window.localStorage.getItem(TIME_RANGE_STORAGE_KEY);
  } catch {
    current = memorySelection ? JSON.stringify(memorySelection) : null;
  }
  if (current === serialized) return false;

  try {
    window.localStorage.setItem(TIME_RANGE_STORAGE_KEY, serialized);
    memorySelection = null;
  } catch {
    memorySelection = selection;
  }
  notifyChange();
  return true;
}

/** Subscribe to changes from this tab (custom event) and other tabs (`storage`). */
export function subscribeTimeRange(callback) {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (event) => {
    // key === null: another tab cleared storage wholesale.
    if (event.key === null || event.key === TIME_RANGE_STORAGE_KEY) callback();
  };
  window.addEventListener(TIME_RANGE_CHANGE_EVENT, callback);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(TIME_RANGE_CHANGE_EVENT, callback);
    window.removeEventListener('storage', onStorage);
  };
}

/* ------------------------------------------------------------- URL override */

const toParams = (source) => {
  if (source && typeof source.get === 'function') return source; // URLSearchParams
  if (typeof source === 'string') return new URLSearchParams(source);
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams();
  }
};

// start_date / end_date arrive as local YYYY-MM-DD (FinOps) or ISO (Alerts).
const parseDateParam = (value, endOfDay) => {
  const text = String(value || '').trim();
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (ymd) {
    const [y, m, d] = [Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])];
    return endOfDay ? new Date(y, m, d, 23, 59, 59, 999) : new Date(y, m, d, 0, 0, 0, 0);
  }
  const ms = Date.parse(text);
  return Number.isFinite(ms) ? new Date(ms) : null;
};

// Legacy ?days=N. Recognized spans stay presets; other spans resolve once per URL in the hook.
const selectionForDays = (days) => {
  const preset = Object.keys(TIME_RANGE_DAYS).find((key) => TIME_RANGE_DAYS[key] === days);
  if (preset) return { preset };
  const end = new Date();
  const start = new Date(end.getTime() - days * 86_400_000);
  return { preset: 'custom', start_time: start.toISOString(), end_time: end.toISOString() };
};

/**
 * The range a URL explicitly asks for, or null when it carries none.
 * `source` is URLSearchParams, a search string, or omitted for window.location.
 *
 * Precedence: absolute start_time/end_time, then time_range=custom with
 * start_date/end_date, then legacy days, then a time_range preset. (`days`
 * outranks `time_range` because old URLs carried both, and there `days` was
 * the Atlas selection while `time_range` was the Header's stale stamp.)
 */
export function readUrlTimeRange(source) {
  const params = toParams(source);

  const absolute = toTimeRangeSelection({
    preset: 'custom',
    start_time: params.get('start_time'),
    end_time: params.get('end_time'),
  });
  if (absolute) return absolute;

  const timeRange = params.get('time_range');
  if (timeRange === 'custom') {
    const start = parseDateParam(params.get('start_date'), false);
    const end = parseDateParam(params.get('end_date'), true);
    const custom = start && end
      ? toTimeRangeSelection({ preset: 'custom', start_time: start.toISOString(), end_time: end.toISOString() })
      : null;
    if (custom) return custom;
  }

  const days = Number.parseInt(params.get('days') || '', 10);
  if (Number.isFinite(days) && days > 0) return selectionForDays(days);

  return toTimeRangeSelection(timeRange);
}

/**
 * The effective range as a preset: URL override → stored selection → fallback.
 * For code outside render (fetch callbacks, event handlers). Components should
 * use useGlobalTimeRange instead, which re-renders on change and persists a
 * URL override.
 */
export function getTimeRange(source, fallback = DEFAULT_TIME_RANGE) {
  return selectionPreset(readUrlTimeRange(source))
    || selectionPreset(readStoredTimeRange())
    || fallback;
}

/** The stored selection as a preset, or null when nothing is stored. */
export const getStoredTimeRangePreset = () => selectionPreset(readStoredTimeRange());

/**
 * Routes that follow the global range: the complement of the Header's old
 * stamping exclusions — modules with their own time handling (SAST, Gemini
 * Enterprise agents, SaaS-AI) and routes where the global filter is hidden
 * (Agent Atlas has its own control; scan-centric pages don't filter by time).
 * For shared components mounted in both worlds, so module pages keep their
 * own defaults instead of silently adopting the global range.
 */
export const isGlobalTimeRangePath = (pathname = '') => !(
  pathname.startsWith('/sast/')
  || pathname.startsWith('/gemini/agents')
  || pathname.startsWith('/saas-ai')
  || isGlobalFilterHiddenForPath(pathname)
);
